import React from 'react';
import { Alert, Badge, Button, Card, Group, List, Text, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconSparkles } from '@tabler/icons-react';

// Advisory NLU auditor grade (§Stage 4.1) — the promised NLU Auditor's output, persisted on the Design
// Model as `nluQualityGrade` ({ score, summary, findings[], lint }). Rendered on the Design tab with a
// one-click "Regenerate addressing N findings" that seeds a bounded NLU refine with the findings text.
const GRADE_COLOR = { pass: 'green', warn: 'yellow', fail: 'red' };
const SEV_COLOR = { high: 'red', medium: 'orange', low: 'dimmed' };

// Compact one-line summary of the deterministic lint (the auditor's numeric half).
function lintLine(lint) {
  if (!lint) return null;
  const bits = [];
  if (lint.totalUtterances != null) bits.push(`${lint.totalUtterances} utterance(s)`);
  if (lint.slotAnnotationCoverage != null) bits.push(`${Math.round(lint.slotAnnotationCoverage * 100)}% slot-annotated`);
  if (lint.balanceRatio != null) bits.push(`${lint.balanceRatio}:1 balance`);
  if (lint.duplicateUtterances) bits.push(`${lint.duplicateUtterances} duplicate`);
  if (lint.leadingArticles) bits.push(`${lint.leadingArticles} leading-article`);
  if (lint.intentsWithoutUtterances) bits.push(`${lint.intentsWithoutUtterances} intent(s) with no utterances`);
  if ((lint.synonymConflicts || []).length) bits.push(`${lint.synonymConflicts.length} synonym conflict(s)`);
  return bits.join(' · ');
}

export default function NluGradeCard({ grade, onRegenerate, disabled }) {
  if (!grade || !grade.score) return null;
  const color = GRADE_COLOR[grade.score] || 'gray';
  const findings = grade.findings || [];
  const lint = lintLine(grade.lint);

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" align="flex-start" mb="xs">
        <Group gap="xs" align="center">
          <Text fw={600}>NLU audit</Text>
          <Badge variant="light" color={color}>{grade.score}</Badge>
          {findings.length > 0 && <Badge variant="light" color="gray">{findings.length} finding(s)</Badge>}
        </Group>
        {findings.length > 0 && onRegenerate && (
          <Tooltip
            label="Re-runs a Design refine seeded with these findings so the NLU slices address them, then re-grades."
            withArrow multiline w={280}
          >
            <Button size="xs" variant="light" color="orange" leftSection={<IconSparkles size={14} />}
              disabled={disabled} onClick={onRegenerate}>
              Regenerate addressing {findings.length} finding(s)
            </Button>
          </Tooltip>
        )}
      </Group>

      {grade.summary && <Text size="sm" c="dimmed" mb={findings.length ? 'xs' : 0}>{grade.summary}</Text>}
      {lint && <Text size="xs" c="dimmed" mb={findings.length ? 'xs' : 0}>Lint: {lint}</Text>}

      {findings.length > 0 ? (
        <List size="sm" spacing={4}>
          {findings.map((f, i) => (
            <List.Item key={i}>
              <Text span fw={600} c={SEV_COLOR[f.severity] || 'dimmed'} size="sm">[{f.severity || 'note'}] </Text>
              {f.collection ? <Text span size="sm" c="dimmed">{f.collection}: </Text> : null}
              {f.issue}{f.fix ? <Text span c="dimmed" size="sm"> — {f.fix}</Text> : null}
            </List.Item>
          ))}
        </List>
      ) : (
        <Alert color="green" icon={<IconCheck size={16} />} variant="light">
          The generated NLU design meets the applied Lex rubric.
        </Alert>
      )}
      {grade.score === 'fail' && (
        <Text size="xs" c="dimmed" mt="xs">
          <IconAlertCircle size={12} style={{ verticalAlign: 'middle' }} /> Advisory only — a fail never
          blocks the design; it flags NLU quality gaps for you to address.
        </Text>
      )}
    </Card>
  );
}
